import type { Tint } from '../core/viewer.ts';

/* Pure mapping from platform state to what the viewer shows. Absent or unknown stays unknown. */

export interface PlaceState {
  code: string;
  booking: 'free' | 'booked' | 'not_bookable';
  readings: Record<string, { value: number; at: string }>;
}

export function tintFor(state: PlaceState | undefined, selected: boolean): Tint {
  if (selected) return 'selected';
  if (!state) return 'unknown';
  if (state.booking === 'free') return 'free';
  if (state.booking === 'booked') return 'booked';
  return 'neutral';
}

/** Applies a snapshot or a delta; a delta older than the snapshot it follows is dropped. */
export function applyUpdate(
  current: { sequence: number; places: Map<string, PlaceState> },
  update: { sequence: number; places: PlaceState[] },
): { sequence: number; places: Map<string, PlaceState>; changed: string[] } {
  if (update.sequence < current.sequence) return { ...current, changed: [] };
  const places = new Map(current.places);
  for (const place of update.places) places.set(place.code, place);
  return { sequence: update.sequence, places, changed: update.places.map((place) => place.code) };
}

export type Metric = 'comfort_index' | 'co2' | 'humidity' | 'temperature';
export type Rating = 'good' | 'fair' | 'poor';

/**
 * How a reading rates on the Layers sheet's scale (Excellent, Acceptable, Poor). The bands follow the
 * gateway's comfort class (@budovan/demo-data comfortClass): CO2 under 800 ppm and 20.5 to 24 °C are
 * good, under 1200 ppm and 19.5 to 25.5 °C acceptable; humidity 40 to 60 % good, 30 to 70 % acceptable.
 */
export function rate(metric: Metric, value: number): Rating {
  const within = (good: [number, number], fair: [number, number]): Rating => {
    if (value >= good[0] && value <= good[1]) return 'good';
    return value >= fair[0] && value <= fair[1] ? 'fair' : 'poor';
  };
  switch (metric) {
    case 'comfort_index':
      return within([3, Infinity], [2, Infinity]);
    case 'co2':
      return within([0, 799], [0, 1199]);
    case 'temperature':
      return within([20.5, 24], [19.5, 25.5]);
    case 'humidity':
      return within([40, 60], [30, 70]);
  }
}

const UNITS: Record<string, string> = { temperature: '°C', co2: 'ppm', humidity: '%', comfort_index: '' };

/** A reading as a tag shows it: 820 ppm, 22.4 °C, 45 %, or the comfort class's word. */
export function formatReading(metric: Metric, value: number): string {
  if (metric === 'comfort_index')
    return { good: 'Excellent', fair: 'Acceptable', poor: 'Poor' }[rate(metric, value)];
  const digits = metric === 'temperature' ? 1 : 0;
  return `${value.toFixed(digits)} ${UNITS[metric]}`;
}

export function describeReadings(readings: PlaceState['readings']): string[] {
  return Object.entries(readings).map(
    ([metric, reading]) =>
      `${metric.replace('_', ' ')} ${Math.round(reading.value * 10) / 10}${UNITS[metric] ?? ''}`,
  );
}
